"use client";

import {
  clearStoredIdentityKey,
  decryptMessage,
  derivePublicKeyFromStoredPrivateKey,
  encryptMessage,
  generateSessionKey,
  importPublicKey,
  loadOrCreateIdentityKey,
  unwrapSessionKey,
  unwrapSessionKeyExtractable,
  wrapSessionKeyForRecipient,
} from "./e2e-client";
import { base64ToBuffer, bufferToBase64 } from "./binary";
import { generatePairingSecret, signBackfillRequest, verifyBackfillRequest } from "./pairing";

function keyHolderIdStorageKey(listingId: string): string {
  return `agenzax_e2e_keyholder_id_${listingId}`;
}

function pairingSecretStorageKey(listingId: string): string {
  return `agenzax_e2e_psk_${listingId}`;
}

/** 이 브라우저가 이 리스팅의 PSK를 알고 있으면 반환한다(없으면 null) — 이 리스팅 최초 등록자로서
 * 직접 생성했거나, 다른 기기로부터 받아서 storePairingSecret으로 저장해둔 값. 다른 디바이스를
 * 페어링할 때 오너에게 보여줄 값. */
export function getStoredPairingSecret(listingId: string): string | null {
  return localStorage.getItem(pairingSecretStorageKey(listingId));
}

/**
 * 실사용 중 발견된 갭: 원래 PSK를 최초 생성한 쪽(주로 에이전트)이 그 값을 소유한 채였고,
 * 나중에 합류한 다른 기기(주로 오너 브라우저)는 요청/승인 한 번 쓰고 버렸다 — 그래서 최초
 * 생성자의 로컬 상태(에이전트의 STATE_DIR 등)가 사라지면, 이미 정상적으로 히스토리를 갖고
 * 있는 다른 키 보유자가 있어도 PSK 자체가 아무 데도 안 남아 새 기기를 더는 들일 수 없었다.
 * PSK는 서버가 검증하는 게 아니라 클라이언트끼리 로컬로 대조하는 값이라(설계 문서 6절 —
 * "서버는 서명을 검증할 방법이 없다"), 한 번 알게 된 값을 지우지 않고 저장해두기만 해도 이
 * 기기가 이후로 계속 승인자 역할을 할 수 있다 — 별도의 "재발급" 개념이 필요 없다.
 */
export function storePairingSecret(listingId: string, pairingSecret: string): void {
  localStorage.setItem(pairingSecretStorageKey(listingId), pairingSecret);
}

export interface IdentityKeyRecord {
  id: string;
  key_holder_role: "agent" | "owner_view";
  device_label: string | null;
  public_key: string; // base64
}

/** 리스팅에 등록된(폐기되지 않은) 모든 키 보유자를 조회한다 — 팬아웃으로 감쌀 대상 전부. */
export async function fetchIdentityKeys(listingId: string): Promise<IdentityKeyRecord[]> {
  const res = await fetch(`/api/listings/${listingId}/identity-keys`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to fetch identity key list.");
  return json.keys;
}

// React StrictMode(개발 모드)나 같은 페이지에서의 동시 호출로 이 함수가 겹쳐 실행되면,
// 아직 localStorage에 key_holder_id가 안 써진 타이밍에 둘 다 "새 키가 필요하다"고 판단해서
// 실제로 서로 다른 키 보유자를 중복 등록해버리는 경쟁 상태가 있었다(실사용 중 발견) — 리스팅당
// 진행 중인 등록 하나만 공유하도록 인플라이트 프라미스로 막는다.
const inFlightRegistrations = new Map<string, Promise<{ privateKey: CryptoKey; keyHolderId: string }>>();

/**
 * 내(이 브라우저) 신원 키를 준비하고, 새로 만든 경우 "새 키 보유자"로 서버에 등록한다(멱등 —
 * 이미 로컬에 있으면 서버 재등록 없이 저장된 key_holder_id를 그대로 씀). Agenzax_E2E_멀티키_설계.md:
 * 예전처럼 리스팅의 기존 공개키를 덮어쓰지 않는다 — 에이전트가 만든 키가 있어도 내 브라우저는
 * 독립된 별도 키 보유자(owner_view)로 추가될 뿐이다.
 */
export async function ensureIdentityKeyRegistered(listingId: string): Promise<{ privateKey: CryptoKey; keyHolderId: string }> {
  const inFlight = inFlightRegistrations.get(listingId);
  if (inFlight) return inFlight;
  const promise = ensureIdentityKeyRegisteredUncached(listingId).finally(() => inFlightRegistrations.delete(listingId));
  inFlightRegistrations.set(listingId, promise);
  return promise;
}

async function ensureIdentityKeyRegisteredUncached(listingId: string): Promise<{ privateKey: CryptoKey; keyHolderId: string }> {
  const { privateKey, publicKeySpki } = await loadOrCreateIdentityKey(listingId);
  if (publicKeySpki) {
    // 이 리스팅에 아직 키 보유자가 하나도 없으면, 지금 등록하는 이 키가 첫 번째다 — 나중에
    // 다른 디바이스(에이전트 포함)를 페어링할 수 있도록 PSK를 지금 미리 만들어 로컬에 저장해둔다
    // (Agenzax_E2E_멀티키_설계.md 6절: "첫 번째 키 보유자를 만드는 클라이언트가 PSK를 생성").
    // 리스팅을 웹으로 먼저 만들고 에이전트를 나중에 붙이는 순서에서 필요한 경로.
    const existingBeforeRegistration = await fetchIdentityKeys(listingId);
    if (existingBeforeRegistration.length === 0 && !getStoredPairingSecret(listingId)) {
      localStorage.setItem(pairingSecretStorageKey(listingId), generatePairingSecret());
    }

    const res = await fetch(`/api/listings/${listingId}/identity-keys`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ public_key: bufferToBase64(publicKeySpki) }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error?.message ?? "Failed to register identity key.");
    localStorage.setItem(keyHolderIdStorageKey(listingId), json.id);
    return { privateKey, keyHolderId: json.id };
  }
  const cachedKeyHolderId = localStorage.getItem(keyHolderIdStorageKey(listingId));
  if (cachedKeyHolderId) return { privateKey, keyHolderId: cachedKeyHolderId };

  // 멀티 키 기능 도입 이전에 이미 등록된 개인키라 key_holder_id 로컬 캐시가 없는 경우 — 개인키에서
  // 공개키를 재도출해 서버에 이미 등록된 것과 일치하는 항목을 찾아 매핑을 복구한다(새로 등록하지
  // 않음 — 이미 존재하는 그 키 보유자 행을 그대로 재사용).
  const derivedPublicKey = await derivePublicKeyFromStoredPrivateKey(listingId);
  if (derivedPublicKey) {
    const derivedPublicKeyB64 = bufferToBase64(derivedPublicKey);
    const existingKeys = await fetchIdentityKeys(listingId);
    const match = existingKeys.find((k) => k.public_key === derivedPublicKeyB64);
    if (match) {
      localStorage.setItem(keyHolderIdStorageKey(listingId), match.id);
      return { privateKey, keyHolderId: match.id };
    }
  }
  // 로컬 개인키가 서버의 어떤 유효한(비폐기) 키와도 대응되지 않는다 — 폐기됐거나 손상된 것으로
  // 보고, 이 개인키를 버리고 새 키를 자동으로 다시 발급받는다. 이전엔 여기서 그냥 에러를 던지고
  // "새 디바이스로 다시 등록하라"고만 안내했는데, 실제로 그 "재등록"을 사람이 개발자도구로
  // localStorage를 직접 지워야만 할 수 있었다 — 조용한 실패는 아니지만 불필요한 수동 조치라
  // 자동 복구로 바꾼다(clearStoredIdentityKey 후 재귀 호출 한 번으로 새 키 생성+등록됨).
  clearStoredIdentityKey(listingId);
  return ensureIdentityKeyRegisteredUncached(listingId);
}

/**
 * 이 브라우저(새 디바이스)가 이 리스팅의 과거 대화 히스토리를 요청한다. PSK는 이미 히스토리를
 * 갖고 있는 다른 디바이스(주로 에이전트)에서 미리 전달받은 값 — 서버에는 절대 보내지 않고,
 * 그 값으로 서명만 만들어서 보낸다(Agenzax_E2E_멀티키_설계.md 6절). 서버는 서명을 검증할
 * 방법이 없으므로 그대로 중계만 하고, 실제 검증·백필 실행은 기존 키 보유자가 한다.
 */
export async function requestBackfillForThisDevice(listingId: string, pairingSecret: string): Promise<void> {
  const { keyHolderId } = await ensureIdentityKeyRegistered(listingId);
  const myKeys = await fetchIdentityKeys(listingId);
  const mine = myKeys.find((k) => k.id === keyHolderId);
  if (!mine) throw new Error("Could not find my own key holder record on the server.");

  const timestamp = Date.now();
  const signature = await signBackfillRequest(pairingSecret, mine.public_key, timestamp);

  const res = await fetch(`/api/listings/${listingId}/backfill-requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ requesting_key_holder_id: keyHolderId, signature, timestamp }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Backfill request failed.");

  // 실사용 중 발견된 갭 수정: 이 PSK를 받은 게 처음이라면(최초 생성자가 아니라 전달받은 값)
  // 버리지 않고 저장해둔다 — 최초 생성자(주로 에이전트)가 나중에 상태를 잃어버려도, 이미
  // 정상적으로 합류했던 이 기기가 이후 새 기기를 계속 들일 수 있게.
  storePairingSecret(listingId, pairingSecret);
}

export interface BackfillRequestRecord {
  id: string;
  created_at: string;
  payload: {
    requesting_key_holder_id: string;
    requesting_public_key: string;
    signature: string;
    timestamp: number;
  };
}

/** 이 리스팅으로 온, 아직 처리 안 한 백필 요청 목록(대시보드 "기기 연결 요청" 화면용). */
export async function listPendingBackfillRequests(listingId: string): Promise<BackfillRequestRecord[]> {
  const res = await fetch(`/api/listings/${listingId}/backfill-requests`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to fetch backfill requests.");
  return json.requests;
}

/**
 * 오너가 저장된 PSK로 요청을 직접 검증한 뒤(서버는 이 검증을 못 함), 이 브라우저가 아는 과거
 * 세션키들을 새 키 보유자의 공개키로 재감싸 제출한다 — company-agent-cli.ts의 respond-backfill과
 * 동일한 로직의 브라우저 버전. 리스팅을 웹으로 먼저 시작한 경우 이 브라우저가 응답자가 된다.
 */
export async function respondToBackfillRequest(listingId: string, pairingSecret: string, request: BackfillRequestRecord): Promise<number> {
  const { requesting_key_holder_id, requesting_public_key, signature, timestamp } = request.payload;
  const valid = await verifyBackfillRequest(pairingSecret, requesting_public_key, timestamp, signature);
  if (!valid) throw new Error("Signature verification failed — this pairing code does not match the request.");

  const { keyHolderId: myKeyHolderId, privateKey } = await ensureIdentityKeyRegistered(listingId);
  const sessionsRes = await fetch(`/api/listings/${listingId}/sessions`);
  const sessionsJson = await sessionsRes.json();
  if (!sessionsRes.ok) throw new Error(sessionsJson.error?.message ?? "Failed to fetch session list.");
  const mine = (sessionsJson.sessions as { session_id: string; epoch: number; key_holder_id: string }[]).filter(
    (s) => s.key_holder_id === myKeyHolderId
  );

  const targetPublicKey = await importPublicKey(base64ToBuffer(requesting_public_key));
  const wraps = [];
  for (const s of mine) {
    const keyRes = await fetch(`/api/sessions/${s.session_id}/key?key_holder_id=${myKeyHolderId}`);
    const keyJson = await keyRes.json();
    if (!keyRes.ok) continue; // 이미 지운 세션 등 — 건너뜀(전체 실패로 만들지 않음)
    const sessionKey = await unwrapSessionKeyExtractable(base64ToBuffer(keyJson.encrypted_session_key), privateKey);
    const rewrapped = await wrapSessionKeyForRecipient(sessionKey, targetPublicKey);
    wraps.push({ session_id: s.session_id, epoch: s.epoch, encrypted_session_key: bufferToBase64(rewrapped) });
  }
  if (wraps.length === 0) return 0;

  const res = await fetch(`/api/listings/${listingId}/backfill-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target_key_holder_id: requesting_key_holder_id, wraps, request_event_id: request.id }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to submit backfill.");
  return json.backfilled;
}

async function wrapForAllHolders(sessionKey: CryptoKey, keys: IdentityKeyRecord[]): Promise<{ key_holder_id: string; encrypted_session_key: string }[]> {
  return Promise.all(
    keys.map(async (k) => ({
      key_holder_id: k.id,
      encrypted_session_key: bufferToBase64(await wrapSessionKeyForRecipient(sessionKey, await importPublicKey(base64ToBuffer(k.public_key)))),
    }))
  );
}

/** "대화 시작" 버튼 클릭 시: 내 키 준비 → 양쪽 리스팅의 등록된 키 보유자 전부 조회 → 세션키 생성 →
 * 전부에 대해 팬아웃으로 감싸기 → 첫 메시지 암호화 → 서버 전송. */
export async function startConversation(myListingId: string, targetListingId: string, plaintext: string, contentType: "text" | "contact_card_request" | "contact_card" = "text") {
  await ensureIdentityKeyRegistered(myListingId); // 없으면 생성+서버 등록(부수효과) — 개인키 자체는 여기서 안 씀
  const [myKeys, targetKeys] = await Promise.all([fetchIdentityKeys(myListingId), fetchIdentityKeys(targetListingId)]);
  if (targetKeys.length === 0) throw new Error("The other party has no registered identity key yet (their company may not be ready for conversations yet).");

  const sessionKey = await generateSessionKey();
  const wrappedKeys = await wrapForAllHolders(sessionKey, [...myKeys, ...targetKeys]);
  const { ciphertext, iv } = await encryptMessage(sessionKey, plaintext);

  const res = await fetch("/api/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sender_listing_id: myListingId,
      target_listing_id: targetListingId,
      initial_message: { ciphertext: bufferToBase64(ciphertext), iv: bufferToBase64(iv), wrapped_keys: wrappedKeys, content_type: contentType },
    }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to open the conversation.");
  return json;
}

/** 백서 4.7.1 자가 테스트(Gate5): 내 리스팅에 스스로 첫 질문을 던져본다. 4.14 "수동 대화 개설을
 * 자기 자신에게 적용한 것"이므로 그 리스팅에 등록된 모든 키 보유자(이 브라우저뿐 아니라 실제
 * 에이전트도)로 팬아웃한다 — 그래야 진짜 에이전트가 알림을 받았을 때 실제로 복호화·응답할 수
 * 있다(브라우저 키만 감싸면 알림은 가도 에이전트가 못 읽어서 응답 불가능했던 버그, 실사용 중 발견). */
export async function startSelfTest(listingId: string, plaintext: string) {
  await ensureIdentityKeyRegistered(listingId); // 없으면 생성+서버 등록(부수효과)
  const myKeys = await fetchIdentityKeys(listingId);
  if (myKeys.length === 0) throw new Error("No identity key is registered for this listing yet.");

  const sessionKey = await generateSessionKey();
  const wrappedKeys = await wrapForAllHolders(sessionKey, myKeys);
  const { ciphertext, iv } = await encryptMessage(sessionKey, plaintext);

  const res = await fetch("/api/sessions/self-test", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      listing_id: listingId,
      ciphertext: bufferToBase64(ciphertext),
      iv: bufferToBase64(iv),
      wrapped_keys: wrappedKeys,
    }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to start the self-test session.");
  return json;
}

/** 이미 열린 세션에서 내 디바이스 몫의 세션키를 복원한다(매번 내 개인키로 언랩 — 별도 캐시 없이 단순하게). */
export async function loadSessionKey(sessionId: string, myListingId: string): Promise<CryptoKey> {
  const { privateKey, keyHolderId } = await ensureIdentityKeyRegistered(myListingId);
  const res = await fetch(`/api/sessions/${sessionId}/key?key_holder_id=${keyHolderId}`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to fetch the session key.");
  return unwrapSessionKey(base64ToBuffer(json.encrypted_session_key), privateKey);
}

export async function sendMessage(
  sessionId: string,
  myListingId: string,
  sessionKey: CryptoKey,
  plaintext: string,
  contentType: "text" | "contact_card_request" | "contact_card" = "text"
) {
  const { ciphertext, iv } = await encryptMessage(sessionKey, plaintext);
  const res = await fetch(`/api/sessions/${sessionId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sender_listing_id: myListingId, ciphertext: bufferToBase64(ciphertext), iv: bufferToBase64(iv), content_type: contentType }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error?.message ?? "Failed to send message.");
  return json.message;
}

export interface RawMessage {
  id: string;
  session_id: string;
  sender_listing_id: string;
  ciphertext: string; // base64
  iv: string; // base64
  delivery_status: "delivered" | "held" | "blocked";
  sender_type: "ai" | "human";
  content_type: "text" | "contact_card_request" | "contact_card";
  created_at: string;
  read_at: string | null;
  sender_display_name: string | null;
}

export interface DecryptedMessage extends RawMessage {
  plaintext: string | null;
  decryptFailed: boolean;
}

/** Gate4 필수 실패 시나리오: 복호화 실패를 깨진 문자로 조용히 감추지 않고 명시적으로 표시한다. */
export async function decryptMessages(messages: RawMessage[], sessionKey: CryptoKey): Promise<DecryptedMessage[]> {
  const out: DecryptedMessage[] = [];
  for (const m of messages) {
    try {
      const plaintext = await decryptMessage(sessionKey, base64ToBuffer(m.ciphertext), base64ToBuffer(m.iv));
      out.push({ ...m, plaintext, decryptFailed: false });
    } catch {
      out.push({ ...m, plaintext: null, decryptFailed: true });
    }
  }
  return out;
}
